import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, 
  Users, 
  Monitor, 
  Megaphone, 
  Calendar,
  CheckSquare,
  BarChart3,
  CreditCard,
  LogOut,
  TrendingUp,
  Clock,
  FileText,
  AlertTriangle,
  Loader2,
  Plus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ParkWiseLogo } from "../../../components/ui/parkwise-logo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const navItems = [
  { path: "/admin/advertising", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/admin/advertising/advertisers", icon: Users, label: "Advertisers" },
  { path: "/admin/advertising/screens", icon: Monitor, label: "Screens & Inventory" },
  { path: "/admin/advertising/campaigns", icon: Megaphone, label: "Campaigns" },
  { path: "/admin/advertising/calendar", icon: Calendar, label: "Bookings Calendar" },
  { path: "/admin/advertising/approvals", icon: CheckSquare, label: "Artwork Approvals" },
  { path: "/admin/advertising/proof-of-play", icon: BarChart3, label: "Proof of Play" },
  { path: "/admin/advertising/billing", icon: CreditCard, label: "Billing & Invoices" },
];

export default function AdvertisingDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/advertising/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    navigate("/admin/login");
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className="w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <ParkWiseLogo size="md" />
          <p className="text-slate-500 text-xs mt-2">Advertising & Campaigns</p>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive 
                    ? "bg-blue-600/20 text-blue-400" 
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
          
          <div className="pt-4 border-t border-slate-800 mt-4">
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-sm">← Back to Main Admin</span>
            </button>
          </div>
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3 px-2">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full px-2">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">Advertising Dashboard</h1>
              <p className="text-slate-400 mt-1">Manage campaigns, screens, and advertisers</p>
            </div>
            <Button 
              onClick={() => navigate("/admin/advertising/campaigns?new=true")}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="new-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>

          {/* KPI Cards - Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-green-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/campaigns?status=active")}
              data-testid="kpi-active-campaigns"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Active Campaigns</CardTitle>
                <Megaphone className="w-5 h-5 text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats?.active_campaigns || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Currently running</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-yellow-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/campaigns?pending=true")}
              data-testid="kpi-draft-campaigns"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Draft & Pending</CardTitle>
                <FileText className="w-5 h-5 text-yellow-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats?.draft_campaigns || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Awaiting approval or setup</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-orange-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/campaigns?expiring=soon")}
              data-testid="kpi-expiring-campaigns"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Expiring Soon</CardTitle>
                <Clock className="w-5 h-5 text-orange-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats?.expiring_campaigns || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Within 7 days</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-blue-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/screens")}
              data-testid="kpi-total-screens"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Total Screens</CardTitle>
                <Monitor className="w-5 h-5 text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats?.total_screens || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">Ad-enabled screens</p>
              </CardContent>
            </Card>
          </div>

          {/* KPI Cards - Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-purple-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/calendar")}
              data-testid="kpi-occupancy"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Occupancy</CardTitle>
                <BarChart3 className="w-5 h-5 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : `${stats?.occupancy_pct || 0}%`}
                </div>
                <p className="text-xs text-slate-500 mt-1">Screen slots booked</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-emerald-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/billing")}
              data-testid="kpi-revenue"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Revenue This Month</CardTitle>
                <TrendingUp className="w-5 h-5 text-emerald-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : `£${(stats?.revenue_this_month || 0).toLocaleString()}`}
                </div>
                <p className="text-xs text-slate-500 mt-1">Paid invoices</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-cyan-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/proof-of-play")}
              data-testid="kpi-impressions"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Est. Impressions</CardTitle>
                <BarChart3 className="w-5 h-5 text-cyan-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (stats?.estimated_impressions || 0).toLocaleString()}
                </div>
                <p className="text-xs text-slate-500 mt-1">Active campaigns</p>
              </CardContent>
            </Card>

            <Card 
              className="bg-slate-900 border-slate-800 cursor-pointer hover:border-red-500/50 transition-colors"
              onClick={() => navigate("/admin/advertising/billing?status=outstanding")}
              data-testid="kpi-outstanding"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-400">Outstanding</CardTitle>
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">
                  {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats?.outstanding_invoices_count || 0}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  £{(stats?.outstanding_invoices_value || 0).toLocaleString()} unpaid
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions / Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pending Approvals */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-amber-400" />
                  Pending Approvals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <div className="text-4xl font-bold text-amber-400 mb-2">
                    {stats?.pending_approvals || 0}
                  </div>
                  <p className="text-slate-400 text-sm mb-4">Artwork awaiting review</p>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/admin/advertising/approvals")}
                    className="border-slate-700 text-slate-300"
                    data-testid="view-approvals-btn"
                  >
                    Review Artwork
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Active Advertisers */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-400" />
                  Advertisers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <div className="text-4xl font-bold text-blue-400 mb-2">
                    {stats?.total_advertisers || 0}
                  </div>
                  <p className="text-slate-400 text-sm mb-4">Active clients</p>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/admin/advertising/advertisers")}
                    className="border-slate-700 text-slate-300"
                    data-testid="view-advertisers-btn"
                  >
                    Manage Advertisers
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  This Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">New Campaigns</span>
                    <span className="text-white font-medium">{stats?.active_campaigns || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Revenue</span>
                    <span className="text-emerald-400 font-medium">£{(stats?.revenue_this_month || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Impressions</span>
                    <span className="text-white font-medium">{(stats?.estimated_impressions || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Occupancy</span>
                    <span className="text-white font-medium">{stats?.occupancy_pct || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
